import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Search across the Fairplay metaobjects.
 *
 * Shopify's search index covers products, pages, articles and collections -
 * metaobjects are not in it, so the artists were unreachable through the stock
 * predictive search while it happily returned the three placeholder products.
 * The index is built in Liquid (snippets/fairplay-search-index.liquid) and
 * filtered here, entirely client-side. No network request, so results land on
 * the keystroke.
 *
 * @typedef {object} Entry
 * @property {string} k - group heading
 * @property {string} n - name
 * @property {string} s - subtitle
 * @property {string} m - meta line
 * @property {string} u - url
 * @property {string | null} i - thumbnail
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} results
 * @property {HTMLElement} [resetButton]
 * @extends {Component<Refs>}
 */
class FairplaySearchComponent extends Component {
  requiredRefs = ['searchInput', 'results'];

  /** @type {Entry[]} */
  #entries = [];

  /** @type {Entry[]} */
  #matches = [];

  #maxResults = 12;

  connectedCallback() {
    super.connectedCallback();
    this.#loadIndex();
    // The dialog is in the DOM from page load, so an input event can arrive
    // before anything else runs. Render once so the resting state is correct.
    this.#render('');
  }

  #loadIndex() {
    const script = document.getElementById('fairplay-search-index');
    if (!script?.textContent) return;

    try {
      const parsed = JSON.parse(script.textContent);
      if (Array.isArray(parsed)) this.#entries = parsed;
    } catch (error) {
      // A malformed index should leave the modal usable rather than throwing
      // on every keystroke; the empty state covers it.
      console.error('[fairplay-search] could not parse the search index', error);
    }
  }

  /**
   * Casefolded and stripped of diacritics, so "Nicolas" finds "Nicolás" and
   * "Le" finds "Lê" - both are in the artist list, and a visitor typing the
   * name from memory will not reach for the accent.
   * @param {string} value
   * @returns {string}
   */
  #normalise(value) {
    return (value ?? '')
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();
  }

  /** @param {Event} event */
  search = debounce((event) => {
    const input = event?.target;
    this.#render(input instanceof HTMLInputElement ? input.value : this.refs.searchInput.value);
  }, 120);

  resetSearch = () => {
    this.refs.searchInput.value = '';
    this.refs.searchInput.focus();
    this.#render('');
  };

  /**
   * Enter opens the top result. Without this the form would submit to
   * Shopify's /search, which cannot return any of these.
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const first = this.#matches[0];
    if (first?.u) window.location.href = first.u;
  };

  /**
   * @param {string} query
   * @returns {Entry[]}
   */
  #match(query) {
    const needle = this.#normalise(query);
    if (needle.length < 2) return [];

    /** @type {{entry: Entry, rank: number}[]} */
    const scored = [];

    for (const entry of this.#entries) {
      const name = this.#normalise(entry.n);
      const rest = `${this.#normalise(entry.s)} ${this.#normalise(entry.m)}`;

      // Ranked so a name match always outranks one that only hit the
      // subtitle or medium - typing "painting" should not bury Amelia
      // Badenoch under everyone whose bio blurb happens to say painter.
      let rank = -1;
      if (name.startsWith(needle)) rank = 0;
      else if (name.includes(needle)) rank = 1;
      else if (rest.includes(needle)) rank = 2;

      if (rank >= 0) scored.push({ entry, rank });
    }

    scored.sort((a, b) => a.rank - b.rank || a.entry.n.localeCompare(b.entry.n));
    return scored.slice(0, this.#maxResults).map((item) => item.entry);
  }

  /** @param {string} query */
  #render(query) {
    const { results, resetButton } = this.refs;
    const trimmed = (query ?? '').trim();

    if (resetButton) resetButton.hidden = trimmed.length === 0;

    if (trimmed.length < 2) {
      this.#matches = [];
      results.replaceChildren();
      results.dataset.state = 'idle';
      this.refs.searchInput.setAttribute('aria-expanded', 'false');
      return;
    }

    this.#matches = this.#match(trimmed);
    results.dataset.state = this.#matches.length ? 'results' : 'empty';
    this.refs.searchInput.setAttribute('aria-expanded', this.#matches.length ? 'true' : 'false');

    if (!this.#matches.length) {
      const empty = document.createElement('p');
      empty.className = 'fairplay-search__empty';
      empty.setAttribute('role', 'status');
      empty.textContent = `No results for “${trimmed}”`;
      results.replaceChildren(empty);
      return;
    }

    const fragment = document.createDocumentFragment();
    let currentGroup = null;

    for (const entry of this.#matches) {
      if (entry.k !== currentGroup) {
        currentGroup = entry.k;
        const heading = document.createElement('p');
        heading.className = 'fairplay-search__group';
        heading.textContent = currentGroup;
        fragment.append(heading);
      }
      fragment.append(this.#renderResult(entry));
    }

    // One live announcement for the whole set, rather than per row.
    const status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.textContent = `${this.#matches.length} result${this.#matches.length === 1 ? '' : 's'} for ${trimmed}`;
    fragment.append(status);

    results.replaceChildren(fragment);
  }

  /**
   * Built with DOM calls rather than innerHTML: every value here is
   * author-entered metaobject content, and textContent removes any question
   * of it being parsed as markup.
   * @param {Entry} entry
   * @returns {HTMLAnchorElement}
   */
  #renderResult(entry) {
    const link = document.createElement('a');
    link.className = 'fairplay-search__result';
    link.href = entry.u || '#';

    if (entry.i) {
      const image = document.createElement('img');
      image.className = 'fairplay-search__result-image';
      image.src = entry.i;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 56;
      image.height = 56;
      link.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'fairplay-search__result-image fairplay-search__result-image--empty';
      link.append(placeholder);
    }

    const text = document.createElement('span');
    text.className = 'fairplay-search__result-text';

    const name = document.createElement('span');
    name.className = 'fairplay-search__result-name';
    name.textContent = entry.n;
    text.append(name);

    const detail = [entry.s, entry.m].filter(Boolean).join(' · ');
    if (detail) {
      const meta = document.createElement('span');
      meta.className = 'fairplay-search__result-meta';
      meta.textContent = detail;
      text.append(meta);
    }

    link.append(text);
    return link;
  }
}

if (!customElements.get('fairplay-search')) {
  customElements.define('fairplay-search', FairplaySearchComponent);
}
